import React, { createContext, useContext, useRef } from "react";
import { produce } from "immer";
import { createStore, useStore } from "zustand";
import { devtools } from "zustand/middleware";

import { createFuzzySearch } from "@hsl/core/fund-explorer/search";
import {
    fetchFundExplorerData,
    indexData,
} from "@hsl/core/fund-explorer/services";
import { type TopLevelDataObj } from "@hsl/core/fund-explorer/types";

import { useAllTranslations } from "../../hooks/useTranslation";
import { searchHelper } from "./helpers";
import {
    type FundExplorerType,
    type FundFinderStore,
    type StoreConfig,
} from "./types";

export const createFundFinderStore = () =>
    createStore<FundFinderStore>()(
        devtools(
            (set, get) =>
                ({
                    baseurl: "",
                    config: [],
                    rawData: [],
                    indexedData: null,
                    loading: true,
                    errors: [],
                    messages: [],
                    search: {
                        searchFn: () => [],
                        term: "",
                        results: undefined,
                        isActive: false,
                        rawResults: [],
                        searchOn: [
                            "productType",
                            "name",
                            "portfolio_name",
                            "isin",
                            "scopeCode",
                            "benchmark",
                            "iterationName",
                            "capabilityStyle",
                            "assetClassStyle",
                            "bloomberg",
                        ],
                    },
                    translations: {},
                    async fetchRawData(config) {
                        const {
                            baseurl,
                            id,
                            audience,
                            language,
                            type,
                            explorerURL,
                        } = config;
                        try {
                            const rawData = await fetchFundExplorerData(
                                `${baseurl}/srp/api/fund-centre/${id}?audience=${audience}&language=${language}`,
                            );
                            set(
                                produce((state: FundFinderStore) => {
                                    state.rawData.push({
                                        ...rawData,
                                        type,
                                        explorerURL,
                                    });
                                }),
                                false,
                                {
                                    type: `Fetch explorer data: ${baseurl}, ${type}, id: ${id}, aud: ${audience}, lang: ${language}`,
                                },
                            );
                        } catch (err) {
                            console.error(err);
                        }
                    },
                    indexData() {
                        set(
                            produce((state: FundFinderStore) => {
                                const { rawData, indexedData } = get();
                                state.indexedData = rawData
                                    ? rawData.map(
                                          ({
                                              type,
                                              funds,
                                              metadata,
                                              explorerURL: url,
                                          }) => ({
                                              type,
                                              data: indexData({
                                                  funds,
                                                  metadata,
                                              }),
                                              url,
                                          }),
                                      )
                                    : indexedData;
                            }),
                            false,
                            { type: "index data" },
                        );
                    },
                    createSearch() {
                        set(
                            produce((state: FundFinderStore) => {
                                state.search.searchFn = createFuzzySearch(
                                    get().search.searchOn,
                                    get()
                                        .indexedData!.map((d) => d.data)
                                        .reduce(
                                            (acc, d) => ({ ...acc, ...d }),
                                            {},
                                        ),
                                    3,
                                );
                            }),
                            false,
                            { type: "Create search" },
                        );
                    },
                    async init(configs) {
                        try {
                            await Promise.all(
                                configs.map((c) => get().fetchRawData(c)),
                            );
                            get().indexData();
                            get().createSearch();
                        } catch (err) {
                            console.log(err);
                            set(() => ({
                                errors: [{ detail: JSON.stringify(err) }],
                            }));
                        } finally {
                            set(() => ({ loading: false }), false, {
                                type: "init: loading complete",
                            });
                        }
                    },
                    getDataById(id) {
                        const match = get().getDataByIdWithType(id);
                        if (!match) {
                            return;
                        }
                        return match.data;
                    },
                    getDataByIdWithType(id) {
                        const { indexedData } = get();
                        if (!indexedData) {
                            return undefined;
                        }
                        const { type, data, url } = indexedData.reduce(
                            (match, { data, type, url }) => {
                                if (match.type) {
                                    return match;
                                }
                                const datum = data[id];
                                return datum
                                    ? { type, url, data: datum }
                                    : match;
                            },
                            { type: undefined, data: {}, url: undefined } as {
                                type: FundExplorerType | undefined;
                                data: TopLevelDataObj | undefined;
                                url: string | undefined;
                            },
                        );
                        return type && data
                            ? {
                                  type,
                                  data,
                                  url,
                              }
                            : undefined;
                    },
                    applySearch(searchTerm: string) {
                        const _term = searchTerm;
                        const reset = _term.length === 0;
                        set(
                            produce((state: FundFinderStore) => {
                                const {
                                    isActiveSearch,
                                    rawResults,
                                    searchResults,
                                } = searchHelper(get(), _term.trim(), reset);
                                state.search.isActive = isActiveSearch;
                                state.search.rawResults = rawResults;
                                state.search.results = searchResults;
                                state.search.term = _term;
                            }),
                            false,
                            {
                                type: reset
                                    ? "Reset search"
                                    : `Search: ${_term}`,
                            },
                        );
                    },
                    setTranslations(translations: Record<string, string>) {
                        set(
                            produce((state: FundFinderStore) => {
                                state.translations = translations;
                            }),
                            false,
                            { type: "Set translations" },
                        );
                    },
                    getAbsoluteURL(
                        relURL,
                        root = "https://fundcentres.lgim.com",
                    ) {
                        return `${root}${relURL}`;
                    },
                    getExplorerURL(type) {
                        return get().indexedData?.find((d) => d.type === type)
                            ?.url;
                    },
                }) as FundFinderStore,
            {
                name: "FundFinderStore",
                enabled: process.env.NODE_ENV !== "production",
            },
        ),
    );

type StoreType = ReturnType<typeof createFundFinderStore>;

export const FundFinderStoreContext = createContext<StoreType | null>(null);

export const FundFinderStoreProvider = ({
    children,
    configs,
}: {
    children: React.ReactNode;
    configs: StoreConfig[];
}) => {
    const storeRef = useRef<StoreType | null>(null);
    const translations = useAllTranslations();

    if (!storeRef.current) {
        storeRef.current = createFundFinderStore();
        void storeRef.current.getState().init(configs);
        // while most translations use a separate context, this is used
        // for translating the CSV download
        storeRef.current.getState().setTranslations(translations);
    }

    return (
        <FundFinderStoreContext.Provider value={storeRef.current}>
            {children}
        </FundFinderStoreContext.Provider>
    );
};

type ExtractState<S> = S extends {
    getState: () => infer T;
}
    ? T
    : never;

const useFundFinderStore = <T,>(
    selector: (state: ExtractState<StoreType>) => T,
    equalityFn?: (a: T, b: T) => boolean,
) => {
    const store = useContext(FundFinderStoreContext);
    if (!store) {
        throw new Error(
            "useFundFinderStore must be used within a FundFinderStoreContext",
        );
    }
    return useStore(store, selector, equalityFn);
};

export default useFundFinderStore;
